import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import urlJoin from "url-join";

import UpsellModal from "../../shared/upsell-modal";
import { AuthNavConfigData, useAuthNavConfigContext } from "../nav-config";

import { makeFetchNewMenuCallback, PostData, SiteNavigationMenu } from "./callbacks";
import { addGlanceIfNotPresent, emitScreenShareStartEvent, screenShareSessionClose } from "./glance";
import { useUniversalMenu } from "./use-universal-menu";

import costarLogo from "$assets/icons/logo.svg";
import { useStrictI18N, useVisible } from "$client/hooks";
import { ShareScreenModal } from "$client/shared/share-screen-modal";
import { AuthenticatedClientConfig, useConfigContext } from "$common";

// TODO(don): base this off BASE_URL, provide via ClientConfig
const demoUrl = "https://www.costar.com/home/demo";

export interface HamburgerMenuProps {
    hamburgerIconRef: React.RefObject<HTMLElement>;
}

function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export const HamburgerMenu: FC<HamburgerMenuProps> = props => {
    const resources = useStrictI18N();

    const { hamburgerIconRef } = props;
    const menuContainerRef = useRef<HTMLDivElement>(null);

    const { loading, navConfig } = useAuthNavConfigContext();
    const navConfigContextRef = useRef<AuthNavConfigData>({ loading, navConfig });

    useEffect(() => {
        navConfigContextRef.current = { loading, navConfig };
    }, [loading, navConfig]);

    // ===================== USER DATA AND CLIENT CONFIG  ======================

    const config = useConfigContext() as AuthenticatedClientConfig;
    const {
        appFeatures = {},
        options: {
            navLinks,
            hosts,
            screenShare: { glanceSite },
        },
        userPreferences: { CultureName: culture = "en-US" } = {},
    } = config;

    // todo refactor
    // ============================== MENU DATA  ===============================

    // memoize fetchMenuCallback and save the returned data for use elsewhere.
    // This function is only ever called once so no parameter equality check
    // is necessary.
    const menuData = useRef<SiteNavigationMenu>();
    const fetchMenuCallback = useMemo(() => {
        const isLoading = (): Boolean => {
            return navConfigContextRef.current.loading;
        };

        const cachedFetchNewMenuCallback = async (postData: PostData): Promise<SiteNavigationMenu> => {
            while (isLoading()) {
                await wait(100);
            }

            if (menuData.current) {
                return menuData.current;
            }

            const response = await makeFetchNewMenuCallback(config, navConfigContextRef.current.navConfig)(postData);
            menuData.current = response;

            return menuData.current;
        };

        return cachedFetchNewMenuCallback;
    }, [config]);

    // ============================== UPSELL MODAL =============================

    const [isUpsellModalVisible, showUpsellModal, hideUpsellModal] = useVisible(false);

    /**
     * Override "currently selected tab" for sake of upselling.
     *
     * Set to a link id when user clicks on a sidebar menu link they don't have access to.
     * "Restored" to `undefined` when the upsell modal is closed.
     *
     * There is a close, but not exact, mapping between product ids and "link ids".
     * Difference has to do with business decisions that I'm not sure about and
     * hot-potato legacy migrations.
     */
    const [selectedNotAvailable, setSelectedNotAvailable] = useState<string | undefined>();
    const [isScreenShareModalOpen, setScreenShareModalOpen] = useState(false);
    const [screenShareCode, setScreenShareCode] = useState(resources.screenShare.modal.loading());

    const onUpsellSubmit = useCallback(() => {
        hideUpsellModal();
        window.open(demoUrl, "_blank");
    }, [hideUpsellModal]);

    const onUpsellClose = useCallback(() => {
        setSelectedNotAvailable(undefined);
        hideUpsellModal();
    }, [setSelectedNotAvailable, hideUpsellModal]);

    // =========================================================================

    // React-external setup logic to run (once) on mount
    useEffect(() => {
        addGlanceIfNotPresent(glanceSite);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- should only ever run once
    }, []);

    const endSessionHandler = useCallback(() => {
        setScreenShareModalOpen(false);
        screenShareSessionClose();
        setScreenShareCode(resources.screenShare.modal.loading());
    }, [resources.screenShare.modal]);

    const closeModalHandler = useCallback(() => {
        setScreenShareModalOpen(false);
    }, []);

    const newMenuCreate = useCallback(async (container: HTMLElement | null | undefined) => {
        if (!menuData?.current?.MenuItems || menuData.current.MenuItems.length === 0 || !container) {
            return;
        }

        const applyMenuItemBehavior = (item: any, container: HTMLElement, id: string) => {
            const listItem = container.querySelector<HTMLAnchorElement>(`li[id^=${id}] > a`);
            if (!listItem) return;

            if (item.NewTab === true) {
                listItem.target = "_blank";
            }

            if (item.IsActivated !== true) {
                listItem.href = "#";
                listItem.onclick = () => {
                    setSelectedNotAvailable(id.toUpperCase());
                    showUpsellModal();
                };
            }
        };

        menuData.current.MenuItems.forEach((menuItem: any) => {
            applyMenuItemBehavior(menuItem, container, menuItem.MenuItemName);

            menuItem.ChildItems?.forEach((childItem: any) => {
                applyMenuItemBehavior(childItem, container, childItem.MenuItemName);

                childItem.ChildItems?.forEach((childChildItem: any) => {
                    applyMenuItemBehavior(childChildItem, container, childChildItem.MenuItemName);
                });
            });
        });
        const screenShareMatches = container.querySelectorAll<HTMLAnchorElement>("li[id^=Screen_Share] > a");
        const preferencesMenuItemMatches = container.querySelector<HTMLAnchorElement>("li[id^=My_Preferences] > a");

        if (screenShareMatches && screenShareMatches.length > 0) {
            screenShareMatches[0].href = "#cobrowse";
            screenShareMatches[0].onclick = () => {
                setScreenShareModalOpen(true);
                emitScreenShareStartEvent(setScreenShareCode, closeModalHandler);
                return false;
            };
        }

        if (preferencesMenuItemMatches) {
            const navigateToCentralPreferences = () => {
                // TODO: Handle session storage feature toggle
                const goBackUrl = window?.location?.href || "";
                const centralPrefUrl: string = `${navLinks.preferences}?goBackUrl=${goBackUrl}`;
                window?.location?.assign(centralPrefUrl);
            };
            preferencesMenuItemMatches.href = "#";
            preferencesMenuItemMatches.onclick = navigateToCentralPreferences;
        }
    }, []);

    useUniversalMenu(hamburgerIconRef, menuContainerRef, {
        onCreated: newMenuCreate,
        fetchMenuCallback,
        appId: 2,
        culture, // TODO: copied from uui. Needs to become dynamic. Can this be a hyphen-separated culture code?
        logo: costarLogo,
        gatewayUri: navLinks.login, // TODO(don): should this be navLinks.centralAuth?,
        listingUri: navLinks.listingManager,
        homeUri: urlJoin(navLinks.login, "home"),
        pdsUri: navLinks.pds,
        legacyGatewayUri: navLinks.legacyGateway,
        authUri: navLinks.centralAuth,
        leaseDcfUri: navLinks.leaseDcf,
        loopNetUri: navLinks.loopNet,
        propexUri: hosts.propex.uri,
        // contactId,
    });

    return (
        <>
            <UpsellModal
                open={isUpsellModalVisible}
                onClose={onUpsellClose}
                loginUrl={navLinks.login}
                productKey={selectedNotAvailable}
            />
            <div ref={menuContainerRef} />
            <ShareScreenModal
                isOpen={isScreenShareModalOpen}
                endSessionAndClose={endSessionHandler}
                closeModal={closeModalHandler}
                code={screenShareCode}
            />
        </>
    );
};

// const getMenuContainer = (): HTMLElement | null => document?.querySelector(".uui-hamburger-menu") ?? null;
